import { formatDate } from '@/lib/time';

import type { TrainingStage } from './training-stage';
import type { DocumentStatus, FlowHistoryItem } from './types';

// Presentación pura del stepper del ciclo (spec §4.3; G4, cierre F1). Cinco etapas
// (reunión 30:24): elaboración, revisión, aprobación, capacitación y vigencia. Las tres
// primeras se derivan de currentStatus; la capacitación y la vigencia, de la etapa de
// capacitación (estado derivado: firma + disparo, sin estado nuevo del flujo). La fecha
// de cada etapa sale del historial por toStatus o de la apertura de la capacitación.

export type StageState = 'done' | 'now' | 'pending';
export type StepKey = 'borrador' | 'en_revision' | 'en_aprobacion' | 'capacitacion' | 'vigente';

export interface StepperStage {
  key: StepKey;
  label: string;
  state: StageState;
  at: string | null;
  /** Detalle bajo la etapa (avance, sin personas, fecha de vigencia). */
  note: string | null;
  /** Capacitación vencida (desde el día siguiente a la vigencia). */
  overdue: boolean;
}

const LABELS: Record<StepKey, string> = {
  borrador: 'Elaboración',
  en_revision: 'Revisión',
  en_aprobacion: 'Aprobación',
  capacitacion: 'Capacitación',
  vigente: 'Vigencia',
};
const ORDER: StepKey[] = ['borrador', 'en_revision', 'en_aprobacion', 'capacitacion', 'vigente'];

// Índice de la etapa actual según el estado del flujo, sin la capacitación. `vigente` y
// `obsoleto` caen más allá de la última (todo hecho); `anulado` nunca empezó.
const PROGRESS: Record<DocumentStatus, number> = {
  reservado: 0,
  borrador: 0,
  en_revision: 1,
  en_aprobacion: 2,
  vigente: ORDER.length,
  obsoleto: ORDER.length,
  anulado: -1,
};

function stageDate(history: FlowHistoryItem[], status: DocumentStatus): string | null {
  return history.filter((h) => h.toStatus === status).at(-1)?.transitionedAt ?? null;
}

/** Índice actual: firmado y en capacitación → 3; capacitación cerrada sin vigencia → 4. */
function currentIndex(status: DocumentStatus, training: TrainingStage | null): number {
  const base = PROGRESS[status];
  if (training === null || base !== 2 || training.state === 'not_started') {
    return base;
  }
  return training.state === 'in_progress' ? 3 : 4;
}

function trainingNote(t: TrainingStage | null): string | null {
  if (t === null) {
    return null;
  }
  switch (t.state) {
    case 'not_applicable':
      return 'Sin personas a capacitar';
    case 'superseded':
      return 'Cerrada por una versión nueva';
    case 'retired':
      return 'Cerrada por la baja del documento';
    case 'closed_by_quality':
      return 'Cerrada por Calidad';
    case 'unknown':
      return 'Sin registro de capacitación';
    case 'in_progress':
      // G2-C: el LMS no reporta la capacitación (no ve el documento o tiene otra versión).
      if (t.lmsUnreportedReason !== null) {
        return t.lmsUnreportedReason === 'missing'
          ? 'El LMS no reporta el documento'
          : 'El LMS tiene otra versión';
      }
      return t.progress === null
        ? 'Avance no disponible'
        : `${t.progress.completed} de ${t.progress.assigned} personas leyeron`;
    default:
      return null;
  }
}

/** Etapas del stepper con su estado, fecha y detalle, para el estado del documento. */
export function stepperStages(
  currentStatus: DocumentStatus,
  history: FlowHistoryItem[],
  training: TrainingStage | null = null,
): StepperStage[] {
  const current = currentIndex(currentStatus, training);
  return ORDER.map((key, i) => {
    let state: StageState = i < current ? 'done' : i === current ? 'now' : 'pending';
    let at = key === 'capacitacion' ? null : stageDate(history, key);
    let note: string | null = null;
    let overdue = false;
    if (key === 'capacitacion' && training !== null && training.state !== 'not_started') {
      at = training.openedAt;
      note = trainingNote(training);
      // Abierta después de la vigencia: sigue siendo la etapa pendiente (vencida).
      if (training.state === 'in_progress') {
        state = 'now';
        overdue = training.overdue;
      }
    }
    if (key === 'vigente' && state === 'now' && training?.dueDate) {
      note = `Entra en vigencia el ${formatDate(training.dueDate)}`;
    }
    return { key, label: LABELS[key], state, at, note, overdue };
  });
}
